import { XIcon } from "@phosphor-icons/react";

import { Badge, Button, classNames, Input } from "@alpacahq/ui/old";

import type { StripeCoupon } from "~/v2/api/rest/billing";
import { NewWindowLink } from "~/v2/components/common";
import { getPrettyCash } from "~/v2/utils/formatting";
import { SUBSCRIBE_FORM_ID } from "../../../const";
import type { MarketDataPlans } from "../../../util";
import type { PaymentSchedule } from "../../types";

interface SummaryCardProps {
  className?: string;
  isElite: boolean;
  paymentSchedule: PaymentSchedule;
  couponCode: string;
  onCouponCodeChange: (couponCode: string) => void;
  onApplyCoupon: () => void;
  onRemoveCoupon: () => void;
  isApplyCouponLoading: boolean;
  isApplyCouponDisabled: boolean;
  nextBillingDate: string;
  totalPrice: string;
  discount: string;
  appliedCoupon: StripeCoupon | null;
  couponCodeError: string | null;
  plans: MarketDataPlans;
}

export const SummaryCard = ({
  className,
  isElite,
  paymentSchedule,
  couponCode,
  appliedCoupon,
  couponCodeError,
  onCouponCodeChange,
  onApplyCoupon,
  onRemoveCoupon,
  isApplyCouponLoading,
  isApplyCouponDisabled,
  nextBillingDate,
  totalPrice,
  discount,
  plans,
}: SummaryCardProps) => (
  <form
    id={SUBSCRIBE_FORM_ID}
    className={classNames(
      "flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-3",
      className,
    )}
    onSubmit={(event) => event.preventDefault()}
  >
    <h3>Summary</h3>
    <div className="flex items-center justify-between gap-2">
      <p className="m-0 p-0 font-semibold text-gray-700 text-sm">
        Algo Trader Plus
      </p>
      <p className="m-0 p-0 font-semibold text-gray-700 text-sm">
        {getPrettyCash(
          paymentSchedule === "monthly" || isElite
            ? plans.monthly.price
            : // If yearly, add the discount to the price
              plans.yearly.price + plans.yearly.discount,
          { acceptZero: true, minimumFractionDigits: 2 },
        )}
      </p>
    </div>
    {!isElite && paymentSchedule === "yearly" && (
      <div className="flex items-center justify-between gap-2">
        <Badge variant="v2/emerald">1 Month Free</Badge>
        <p className="m-0 p-0 font-semibold text-gray-400 text-sm">
          {getPrettyCash(-plans.yearly.discount, {
            acceptZero: true,
            minimumFractionDigits: 2,
          })}
        </p>
      </div>
    )}
    {isElite && (
      <div className="flex items-center justify-between gap-2">
        <Badge variant="v2/emerald">Free Upgrade</Badge>
        <p className="m-0 p-0 font-semibold text-gray-400 text-sm">
          {getPrettyCash(-plans.yearly.discount, {
            acceptZero: true,
            minimumFractionDigits: 2,
          })}
        </p>
      </div>
    )}
    {!isElite && (
      <div className="flex xs:flex-row flex-col xs:items-start xs:justify-between gap-3">
        <Input
          value={couponCode}
          onChange={(event) => onCouponCodeChange(event.target.value)}
          type="text"
          aria-label="Partner code"
          placeholder="Partner code"
          status={couponCodeError ? "error" : "neutral"}
          errorMsg={couponCodeError}
        />
        <Button
          variant="v2/filled"
          onClick={onApplyCoupon}
          disabled={isApplyCouponDisabled}
          loading={isApplyCouponLoading}
          type="button"
        >
          Apply
        </Button>
      </div>
    )}
    {!isElite && appliedCoupon && (
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center justify-start gap-2">
          <p className="m-0 p-0 font-semibold text-gray-700 text-sm">
            {appliedCoupon.name ?? "Coupon"}
          </p>
          <button
            type="button"
            onClick={onRemoveCoupon}
            aria-label="Remove coupon"
          >
            <XIcon />
          </button>
        </div>

        <p className="m-0 p-0 font-mono text-gray-700 text-sm">{discount}</p>
      </div>
    )}
    <div className="mt-auto flex flex-col gap-3 border-gray-300 border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <p className="m-0 p-0 font-semibold text-gray-700 text-sm">Total</p>
        <p className="m-0 p-0 font-mono font-semibold text-gray-700 text-sm">
          {totalPrice}
        </p>
      </div>
      {!isElite && (
        <div className="flex items-center justify-between gap-2">
          <p className="m-0 p-0 font-semibold text-gray-700 text-sm">
            Next Billing Date
          </p>
          <p className="m-0 p-0 font-mono font-semibold text-gray-700 text-sm">
            {nextBillingDate}
          </p>
        </div>
      )}
      <p className="m-0 inline-flex items-center justify-start gap-1 p-0 font-semibold text-gray-700 text-xs">
        Need help?{" "}
        <NewWindowLink
          to="https://alpaca.markets/contact"
          hasIcon={false}
          className="text-indigo-600"
        >
          Contact Support
        </NewWindowLink>
      </p>
    </div>
  </form>
);
